/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { DateTime } from 'luxon';
import { translate } from '../constants/translated/translations';
import * as types from '../constants/ActionTypes';
const getDefaultFileName = () => `[${translate('FILE_PLACEHOLDER')} ${new Date(DateTime.local())}]`;
export const audioDeviceChanged = id => ({
  type: types.AUDIO_DEVICE_CHANGED,
  id
});
export const audioDeviceDisabled = () => ({
  type: types.AUDIO_DEVICE_DISABLED
});
export const cleanUp = () => ({
  type: types.CLEAN_UP
});
export const unmountCleanUp = () => ({
  type: types.UNMOUNT_CLEAN_UP
});
export const closeClicked = () => {
  return dispatch => {
    dispatch({
      type: types.CLOSE_CLICKED
    });
  };
};
export const countdownComplete = () => ({
  type: types.COUNTDOWN_COMPLETE
});
export const pauseClicked = () => ({
  type: types.PAUSE_CLICKED
});
export const continueClicked = () => ({
  type: types.CONTINUE_CLICKED
});
export const finishClicked = () => {
  return dispatch => {
    dispatch({
      type: types.FINISH_CLICKED,
      fileName: getDefaultFileName()
    });
    dispatch(cleanUp());
  };
};
export const onComplete = () => ({
  type: types.ONCOMPLETE
});
export const startClicked = () => ({
  type: types.START_CLICKED
});
export const startoverClicked = () => ({
  type: types.STARTOVER_CLICKED
});
export const videoDeviceChanged = id => ({
  type: types.VIDEO_DEVICE_CHANGED,
  id
});
export const updateTimer = time => ({
  type: types.UPDATE_TIMER,
  time
});
export const videoDeviceDisabled = () => ({
  type: types.VIDEO_DEVICE_DISABLED
});
export const deviceRequestAccepted = () => ({
  type: types.DEVICE_REQUEST_ACCEPTED
});
export const deviceRequestFailed = () => ({
  type: types.DEVICE_REQUEST_FAILED
});
export const mediaRecorderInitialized = mr => ({
  type: types.MEDIA_RECORDER_INITIALIZED,
  mr
});
export const videoObjectGenerated = (src, blob) => ({
  type: types.VIDEO_OBJECT_GENERATED,
  src,
  blob
});
export const errorOccurred = (msg, isCritical = true) => ({
  type: types.ERROR_OCCURRED,
  msg,
  critical: isCritical
});
export const errorsCleared = () => ({
  type: types.ERROR_CLEARED
});
export const devicesFound = (devices, {
  videoDeviceId,
  audioDeviceId
}) => {
  return dispatch => {
    let selectedVideoId = videoDeviceId;
    if (videoDeviceId === '' && devices.videoinput.length) {
      // when access to webcam is prohibited but mic is allowed
      // browser returns empty stream with '' label
      // but for us it means it's disabled which is null in our system
      // ('' empty string is used for initialization to trigger default device selection)
      selectedVideoId = devices.videoinput[0].deviceId || null;
    }
    let selectedAudioId = audioDeviceId;
    if (audioDeviceId === '') {
      // when access to mic is prohibited but webcam is allowed
      // browser returns empty stream with '' label
      // but for us it means it's disabled which is null in our system
      // ('' empty string is used for initialization to trigger default device selection)
      selectedAudioId = getDefaultAudioDeviceId(devices.audioinput) || null;
    }
    dispatch({
      type: types.DEVICES_FOUND,
      devices: {
        videoinput: devices.videoinput,
        audioinput: filterOutDefaultAudioDeviceId(devices.audioinput, selectedAudioId)
      },
      selectedVideoId: selectedVideoId,
      selectedAudioId: selectedAudioId
    });
  };
};
function getDefaultAudioDeviceId(audioDevices) {
  if (audioDevices.length === 0) return null;

  /*
    MDN (https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices#return_value):
    "The order is significant — the default capture devices will be listed first.""
     Default in this case effectively means the device set as default in browser settings (not obligatory equals default in the OS itself)
  */
  return audioDevices[0].deviceId;

  /* reference to find old code for selecting OS default option */
  // was removed in favour of browser settings. Default OS option is expected to be 1st in the list anyway.
}

// NOTE: it will drop default audioDevice if the related physical device is present
function filterOutDefaultAudioDeviceId(audioDevices, selectedDeviceId) {
  const defaultAudioDeviceId = 'default';
  if (selectedDeviceId === defaultAudioDeviceId) return audioDevices;
  const defaultAudioDeviceIndex = audioDevices.findIndex(d => d.deviceId === defaultAudioDeviceId);
  if (defaultAudioDeviceIndex === -1) return audioDevices;
  const defaultAudioDevice = audioDevices[defaultAudioDeviceIndex];
  const defaultPhysicalAudioDevice = audioDevices.find(d => d.deviceId !== defaultAudioDeviceId && d.groupId === defaultAudioDevice.groupId);
  if (!defaultPhysicalAudioDevice) return audioDevices;
  return audioDevices.filter(d => d.deviceId !== defaultAudioDeviceId);
}
export const saveClicked = fileName => {
  return dispatch => {
    if (fileName.length === 0) {
      dispatch({
        type: types.SAVE_CLICKED,
        fileName: getDefaultFileName()
      });
    } else {
      dispatch({
        type: types.SAVE_CLICKED,
        fileName
      });
    }
    dispatch(onComplete());
  };
};
export const initWebcam = () => ({
  type: types.INITIALIZE_WEBCAM
});
export const activateScreenSharing = () => ({
  type: types.SCREEN_SHARING_ENABLED
});
export const deactivateScreenSharing = () => ({
  type: types.SCREEN_SHARING_DISABLED
});
export const screenSharingEnded = () => ({
  type: types.SCREEN_SHARING_ENDED,
  fileName: getDefaultFileName()
});
export const startMergedStream = () => ({
  type: types.START_MERGED_STREAM
});
export const stopMergedStream = () => ({
  type: types.STOP_MERGED_STREAM
});
export const mergedStreamUpdated = () => ({
  type: types.MERGED_STREAM_UPDATED
});
export const setScreenSharingOptions = options => ({
  type: types.SCREEN_SHARING_EXTRA_OPTIONS_SET,
  options
});
export const setScreenSharingType = screenSharingType => ({
  type: types.SCREEN_SHARING_TYPE_CHANGED,
  screenSharingType
});
export const enablePictureInPicture = () => ({
  type: types.PICTURE_IN_PICTURE_ENABLED
});
export const disablePictureInPicture = () => ({
  type: types.PICTURE_IN_PICTURE_DISABLED
});
export const toggleCameraEffect = effect => ({
  type: types.TOGGLE_CAMERA_EFFECT,
  effect
});
export const setSystemPermissionError = deviceKey => ({
  type: types.SYSTEM_PERMISSION_ERROR,
  device: deviceKey
});